import React from "react";
import { PhoneIncoming, PhoneOutgoing, ArrowLeftRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { CallbackButton } from "@/components/CallFlow";
import { LineBadge } from "@/components/LineBadge";
import { formatCallTime, formatCallDuration, initials, motifLabel } from "@/lib/format";

export const STATUS_META = {
  answered: { label: "Répondu", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  missed: { label: "Manqué", cls: "bg-red-100 text-red-800 border-red-200" },
  voicemail: { label: "Messagerie", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  busy: { label: "Occupé", cls: "bg-slate-200 text-slate-700 border-slate-300" },
  no_answer: { label: "Sans réponse", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <Badge className={`rounded-full px-2.5 py-0.5 text-xs font-medium border hover:bg-current/0 ${m.cls}`}>{m.label}</Badge>;
};

const DIR = {
  inbound: { label: "Entrant", icon: PhoneIncoming, cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  outbound: { label: "Sortant", icon: PhoneOutgoing, cls: "bg-blue-100 text-blue-800 border-blue-200" },
  internal: { label: "Interne", icon: ArrowLeftRight, cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

export const DirectionBadge = ({ direction }) => {
  const d = DIR[direction] || DIR.internal;
  const Icon = d.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium border ${d.cls}`}>
      <Icon className="h-3 w-3" strokeWidth={2.4} />
      {d.label}
    </span>
  );
};

export const CallRow = ({ call, active, onSelect }) => (
  <tr
    data-testid={`call-row-${call.id}`}
    onClick={() => onSelect(call)}
    className={`cursor-pointer border-b border-slate-100 transition-colors ${active ? "bg-emerald-50/70" : "hover:bg-slate-50"}`}
  >
    <td className="py-3.5 pl-5 pr-3">
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-semibold text-slate-500">
          {initials(call.name)}
        </span>
        <div className="leading-tight min-w-0">
          <p className="font-medium text-slate-900 text-sm truncate">{call.name}</p>
          <p className="text-xs text-slate-400 tabular-nums">{call.number}</p>
        </div>
      </div>
    </td>
    <td className="py-3.5 px-3"><DirectionBadge direction={call.direction} /></td>
    <td className="py-3.5 px-3 text-sm text-slate-600 tabular-nums whitespace-nowrap">{formatCallTime(call.started_at)}</td>
    <td className="py-3.5 px-3 text-sm text-slate-600">{call.answered_by || "—"}</td>
    <td className="py-3.5 px-3 text-sm text-slate-600 tabular-nums">{formatCallDuration(call)}</td>
    <td className="py-3.5 pr-5 pl-3 text-right">
      <div className="flex items-center justify-end gap-1.5">
        <LineBadge line={call.line} compact testid={`line-badge-row-${call.id}`} />
        <StatusBadge status={call.status} />
      </div>
    </td>
  </tr>
);

export const CallList = ({ calls, activeId, onSelect }) => (
  <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
    <table className="w-full min-w-[720px] text-left" data-testid="calls-table">
      <thead>
        <tr className="border-b border-slate-200 bg-slate-50/50">
          {["Appelant", "Sens", "Date et heure", "Répondu par", "Durée", "Statut"].map((h, i) => (
            <th key={h} className={`py-3 px-3 text-xs tracking-[0.05em] uppercase font-semibold text-slate-500 ${i === 0 ? "pl-5" : ""} ${i === 5 ? "pr-5 text-right" : ""}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {calls.map((call) => (
          <CallRow key={call.id} call={call} active={call.id === activeId} onSelect={onSelect} />
        ))}
      </tbody>
    </table>
  </div>
);

// Red badge shown when the latest missed/voicemail call was never called back.
export const NeedsCallbackBadge = () => (
  <Badge data-testid="needs-callback-badge" className="rounded-full px-2.5 py-0.5 text-xs font-semibold border bg-red-100 text-red-800 border-red-200 hover:bg-current/0">
    À rappeler
  </Badge>
);

// Small motif tag (from the AI summary of the row's latest call).
export const MotifTag = ({ motif }) => (
  <span data-testid="motif-tag" className="rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap">
    {motifLabel(motif)}
  </span>
);

// Grouped view: one row per phone number, most recent call on top.
const GroupedCallRow = ({ row, active, onSelect }) => (
  <tr
    data-testid={`group-row-${row.number_key}`}
    onClick={() => onSelect(row)}
    className={`cursor-pointer border-b border-slate-100 transition-colors ${active ? "bg-emerald-50/70" : "hover:bg-slate-50"}`}
  >
    <td className="py-3.5 pl-5 pr-3">
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-semibold text-slate-500 shrink-0">
          {initials(row.name)}
        </span>
        <div className="leading-tight min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-slate-900 text-sm truncate">
              {row.name}
              {row.call_count > 1 && <span className="text-slate-400 font-normal"> ({row.call_count})</span>}
            </p>
            {row.motif && <MotifTag motif={row.motif} />}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <p className="text-xs text-slate-400 tabular-nums whitespace-nowrap">{row.number}</p>
            <LineBadge line={row.last_call?.line} compact testid={`line-badge-${row.number_key}`} />
            {row.needs_callback && <NeedsCallbackBadge />}
            {row.needs_callback && (
              <CallbackButton numberKey={row.number_key} number={row.number} name={row.name} line={row.last_call?.line} />
            )}
          </div>
        </div>
      </div>
    </td>
    <td className="py-3.5 px-3"><DirectionBadge direction={row.last_call.direction} /></td>
    <td className="py-3.5 px-3 text-sm text-slate-600 tabular-nums whitespace-nowrap">{formatCallTime(row.last_call.started_at)}</td>
    <td className="py-3.5 pr-5 pl-3 text-right"><StatusBadge status={row.last_call.status} /></td>
  </tr>
);

export const GroupedCallList = ({ rows, activeKey, onSelect }) => (
  <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
    <table className="w-full min-w-[640px] text-left" data-testid="calls-table">
      <thead>
        <tr className="border-b border-slate-200 bg-slate-50/50">
          {["Contact", "Sens", "Date et heure", "Statut"].map((h, i) => (
            <th key={h} className={`py-3 px-3 text-xs tracking-[0.05em] uppercase font-semibold text-slate-500 ${i === 0 ? "pl-5" : ""} ${i === 3 ? "pr-5 text-right" : ""}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <GroupedCallRow key={row.number_key} row={row} active={row.number_key === activeKey} onSelect={onSelect} />
        ))}
      </tbody>
    </table>
  </div>
);
